// src/components/kuestion/shared/Bits.jsx
// Small presentational pieces shared by the examiner and student apps.
import React, { useState } from 'react';
import KuestionMark from '../KuestionMark';
import { InfoIcon, WarnIcon, CheckIcon, CopyIcon } from './Icons';
import { copyText } from './format';

export const Mark = () => (
  <KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" />
);

export const Loading = ({ label = 'Loading' }) => (
  <div className="kqc-loading"><span className="kqc-spinner" />{label}</div>
);

export const Alert = ({ kind = 'info', children, onClose }) => (
  <div className={`kqc-alert${kind === 'error' ? ' kqc-alert--error' : kind === 'success' ? ' kqc-alert--success' : ''}`} role={kind === 'error' ? 'alert' : 'status'}>
    <span className="i">{kind === 'error' ? <WarnIcon /> : kind === 'success' ? <CheckIcon /> : <InfoIcon />}</span>
    <span style={{ flex: 1 }}>{children}</span>
    {onClose && <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} style={{ minHeight: 0, padding: 0 }}>Dismiss</button>}
  </div>
);

export const Note = ({ icon, children, accent, wide, style }) => (
  <div className={`kqc-note${accent ? ' kqc-note--accent' : ''}${wide ? ' kqc-note--wide' : ''}`} style={style}>
    <span className="i">{icon || <InfoIcon />}</span>
    <span>{children}</span>
  </div>
);

export const RuleHead = ({ title, as = 'h2', right }) => {
  const H = as;
  return (
    <div className="kqc-rulehead">
      <H>{title}</H>
      <span className="line" />
      {right}
    </div>
  );
};

export const Stat = ({ label, value, hint, accent, size }) => (
  <div className="kqc-stat">
    <div className="kqc-stat__label">{label}</div>
    <div className={`kqc-stat__value${size ? ` kqc-stat__value--${size}` : ''}`} style={accent ? { color: accent === 'ink' ? 'var(--color-accent-700)' : 'var(--color-accent)' } : undefined}>{value}</div>
    {hint && <div className="kqc-stat__hint">{hint}</div>}
  </div>
);

export const CopyButton = ({ text, label = 'Copy', ink }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={`kqc-copybtn${ink ? ' kqc-copybtn--ink' : ''}`}
      title={`Copy ${text}`}
      onClick={async () => { if (await copyText(text)) { setDone(true); setTimeout(() => setDone(false), 1600); } }}
    >
      {done ? <CheckIcon /> : <CopyIcon />}
      {done ? 'Copied' : label}
    </button>
  );
};

export const Bar = ({ value, weak, none, size }) => (
  <div className={`kqc-bar${size ? ` kqc-bar--${size}` : ''}`}>
    <i className={none ? 'is-none' : weak ? 'is-accent' : ''} style={{ width: `${none ? 0 : Math.max(0, Math.min(100, value || 0))}%` }} />
  </div>
);

export const Dialog = ({ title, danger, icon, onClose, children, width }) => (
  <div className="kqc-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
    <div className="kqc-dialog" role="dialog" aria-modal="true" aria-label={title} style={width ? { maxWidth: width } : undefined}>
      <div className={`kqc-dialog__head${danger ? ' kqc-dialog__head--danger' : ''}`}>
        {icon}
        <span>{title}</span>
      </div>
      <div className="kqc-dialog__body">{children}</div>
    </div>
  </div>
);

// Module strength row shared by cohort, student report and student dashboard.
// `sufficient:false` renders as insufficient data — never as 0%.
export const ModuleRow = ({ m, min, stacked, hint }) => {
  const ok = m.sufficient !== false && m.strength != null;
  const val = ok ? Math.round(Number(m.strength) * 10) / 10 : null;
  const weak = ok && val < 50;
  const counts = ok
    ? `${m.correct ?? '—'} of ${m.attempted ?? '—'} questions correct`
    : `${m.attempted ?? 0} question${m.attempted === 1 ? '' : 's'} attempted — ${min ?? 'more'} needed`;
  if (stacked) {
    return (
      <div className="kqc-modrow kqc-modrow--stack">
        <div className="kqc-modrow__head">
          <strong>{m.module || m.name}</strong>
          <span className={`val${!ok ? ' is-none' : weak ? ' is-weak' : ''}`}>{ok ? `${val}%` : 'Not enough yet'}</span>
        </div>
        <Bar value={val} weak={weak} none={!ok} size="xl" />
        <em>{hint || counts}</em>
      </div>
    );
  }
  return (
    <div className="kqc-modrow">
      <div className="kqc-modrow__name">
        <strong>{m.module || m.name}</strong>
        <em>{counts}</em>
      </div>
      <div className="kqc-modrow__bar">
        <Bar value={val} weak={weak} none={!ok} size="lg" />
        <div className={`kqc-modrow__val${!ok ? ' is-none' : weak ? ' is-weak' : ''}`}>{ok ? `${val}%` : 'Insufficient data'}</div>
      </div>
    </div>
  );
};
